/**
 * Setting the margin and letting the price follow.
 *
 * A rep says what gross profit they want and the line items are scaled to
 * reach it. It cannot be solved in one step — the permit, the GC fee and the
 * insurance are themselves slices of revenue, so raising the price raises the
 * cost — which is `contractBaseForMargin`'s job. This file is the rest of it:
 * what each line becomes, and, when the answer is "it cannot be done", which
 * of the three reasons it is.
 *
 * It lives here rather than in the estimate card because a component that
 * multiplies money is a component that can disagree with the ladder (rule 3),
 * and because "why can this not be done" is a question the screen has to
 * answer in words rather than by doing nothing. Until 2026-09-15 all of this
 * was a `window.prompt` and three `window.alert`s — Jonathon: "it also needs
 * to be more user friendly on the gross profit percentage adjustment. often
 * times sam tries to adjust it but it won't let him."
 */

import {
  contractAmount,
  contractBaseForMargin,
  type LadderInput,
} from "./ladder.js";
import {
  type Cents,
  type UnitRate,
  cents,
  multiplyCents,
  rateFromTotal,
  subCents,
  toCents,
} from "./money.js";

/** What one line item becomes. */
export interface RepricedLine {
  id: string;
  /** The line total, in cents. */
  customerPrice: Cents;
  /** Only when the line already carries one — see `estimate_items`. */
  unitPrice: UnitRate | null;
  /** What it was, so a preview can show the move. */
  wasCustomerPrice: Cents;
}

/**
 * Why a margin cannot be applied.
 *
 * Each is a different thing for a person to do about it, which is the whole
 * reason they are separate values rather than one "no".
 */
export type RepriceRefusal =
  /** Outside 0–100, or not a number at all. */
  | "margin_out_of_range"
  /** Nothing priced yet, so there is nothing to scale. */
  | "no_lines"
  /**
   * Every line is zero. Scaling multiplies, and nothing times anything is
   * still nothing — a job whose lines are all $0 has to be priced before a
   * margin means anything.
   */
  | "lines_are_zero"
  /**
   * The change orders already agreed come to more than the whole contract this
   * margin produces, so there is nothing left for the line items to be.
   *
   * A *higher* margin is what fixes it — a bigger contract leaves room for the
   * change orders inside it — which is the opposite of what anybody guesses,
   * and is why the number that works is worked out and offered rather than
   * left to trial and error.
   */
  | "change_orders_exceed_target";

export interface RepricePlan {
  /** Null when it can be applied. */
  refusal: RepriceRefusal | null;
  /** The margin asked for, 0–1. */
  margin: number;
  /** What the line items have to add up to. */
  targetLineTotal: Cents;
  /** What the contract becomes, GC fee and all. */
  grandTotal: Cents;
  /** What it is now, so a preview can show both. */
  wasGrandTotal: Cents;
  /** What every price is multiplied by. */
  factor: number;
  lines: readonly RepricedLine[];
}

/** The little of a line this needs. `estimate_items`, as fetched. */
export interface RepriceableLine {
  id: string;
  customer_price: unknown;
  unit_price?: unknown;
  qty?: unknown;
  work_doing?: boolean | null;
}

/**
 * A margin typed by a person, as a fraction, or null if it is not one.
 *
 * "40", "40%", " 40.5 % " and "40,5" all mean the same thing — the comma
 * because a phone keyboard offers one and a rep reaching for a decimal point
 * sometimes gets it. What is *not* accepted is 0.4: it is either four tenths
 * of a percent or forty percent depending on who typed it, and a repricing
 * that guessed wrong would be out by a hundred times.
 */
export function parseMarginPercent(text: string): number | null {
  const cleaned = text.replace(/[%\s]/g, "").replace(",", ".");
  if (cleaned === "") return null;
  const value = Number(cleaned);
  if (!Number.isFinite(value)) return null;
  return value;
}

/** Whether a typed percentage is one a job can be sold at. */
export function marginIsInRange(percent: number): boolean {
  return Number.isFinite(percent) && percent > 0 && percent < 100;
}

/**
 * What repricing this estimate to `percent` gross profit would do.
 *
 * Pure, and computed on every keystroke by the screen that offers it, so the
 * rep sees the contract move before anything is written. Nothing here touches
 * the database.
 *
 * Change orders and discounts are left alone: they are numbers somebody
 * already agreed with the customer, and scaling them would quietly rewrite an
 * agreement to hit a percentage.
 */
export interface RepriceTotals {
  lineTotal: Cents;
  changeOrders: Cents;
  lineDiscount: Cents;
  officeDiscount: Cents;
  grandTotal: Cents;
}

export function planReprice(
  input: LadderInput,
  totals: RepriceTotals,
  /**
   * The estimate's line items, as rows. Not `input.items` — the ladder only
   * ever needs four columns of a line and does not carry the id, and this has
   * to name the rows it would write.
   */
  lines: readonly RepriceableLine[],
  percent: number,
): RepricePlan {
  const margin = percent / 100;
  const empty = {
    margin,
    targetLineTotal: 0 as Cents,
    grandTotal: 0 as Cents,
    wasGrandTotal: totals.grandTotal,
    factor: 0,
    lines: [] as RepricedLine[],
  };

  if (!marginIsInRange(percent))
    return { ...empty, refusal: "margin_out_of_range" };

  const doing = lines.filter((item) => item.work_doing !== false);
  if (doing.length === 0) return { ...empty, refusal: "no_lines" };
  if (totals.lineTotal <= 0) return { ...empty, refusal: "lines_are_zero" };

  const targetBase = contractBaseForMargin(input, margin);
  const targetLineTotal = cents(
    targetBase -
      totals.changeOrders +
      totals.lineDiscount +
      totals.officeDiscount,
  );
  if (targetLineTotal <= 0)
    return { ...empty, refusal: "change_orders_exceed_target" };

  const factor = targetLineTotal / totals.lineTotal;

  const repriced: RepricedLine[] = [];
  for (const item of doing) {
    const was = toCents(item.customer_price);
    // A line priced at nothing stays at nothing. Scaling multiplies, so there
    // is no factor that moves it, and writing $0 back over $0 is a write for
    // no reason.
    if (was === 0) continue;
    const next = multiplyCents(was, factor);
    repriced.push({
      id: item.id,
      customerPrice: next,
      // Only where the line already carries a rate. Adding one to a line that
      // never had it would change what the column means on that row.
      unitPrice:
        item.unit_price == null || item.unit_price === ""
          ? null
          : rateFromTotal(next, Number(item.qty ?? 1) || 1),
      wasCustomerPrice: was,
    });
  }

  // What the customer's contract becomes. Not `targetBase`: the GC fee is
  // billed on top of it, and the number the rep is looking at is the one the
  // customer will see. `contractAmount` rather than adding the fee here, so
  // an edited or struck-off fee is honoured exactly as the ladder honours it.
  const { grandTotal } = contractAmount({
    gcFeeOverride: input.autoExpenseOverrides?.gc_fee,
    gcFeeRemoved: input.autoExpenseRemovals?.gc_fee,
    lineTotal: targetLineTotal,
    lineDiscount: totals.lineDiscount,
    changeOrders: totals.changeOrders,
    officeDiscount: totals.officeDiscount,
    jobType: input.jobType,
  });

  return {
    refusal: null,
    margin,
    targetLineTotal,
    grandTotal,
    wasGrandTotal: totals.grandTotal,
    factor,
    lines: repriced,
  };
}

/**
 * The lowest margin this estimate can actually be sold at, or null.
 *
 * Only asked when `discounts_exceed_target` has already said the answer is
 * "not this one". A percentage point at a time, upwards, because the rep needs
 * a number they can press rather than "try something else" — and because the
 * relationship between the margin and what is left for the discounts is not
 * one anybody solves in their head.
 */
export function lowestReachableMargin(
  input: LadderInput,
  totals: RepriceTotals,
  lines: readonly RepriceableLine[],
): number | null {
  for (let percent = 1; percent < 100; percent++) {
    if (planReprice(input, totals, lines, percent).refusal === null)
      return percent;
  }
  return null;
}

/** What to tell somebody, in their own words, when it cannot be done. */
export function repriceRefusalText(
  refusal: RepriceRefusal,
  lowest: number | null,
): string {
  switch (refusal) {
    case "margin_out_of_range":
      return "A margin has to be between 0 and 100.";
    case "no_lines":
      return "There is nothing to reprice yet — add a line item first.";
    case "lines_are_zero":
      return "Every line on this estimate is $0. Price the work first, and the margin will move it.";
    case "change_orders_exceed_target":
      return lowest === null
        ? "The change orders already on this estimate come to more than any margin leaves room for. Take one of them off first."
        : `The change orders already on this estimate come to more than a contract at this margin, so there is nothing left for the line items. ${lowest}% is the lowest that works.`;
  }
}

/**
 * Which way the contract moves, and by how much, for the preview.
 *
 * The margin form shows "Goes up by" or "Comes down by" and an amount. That
 * was a subtraction on two money values inside the component (review of #255,
 * rule 3); it is here so the preview and anything else that says it are the
 * same sum. `by` is never negative — `direction` says which way.
 */
export function repriceMove(
  plan: Pick<RepricePlan, "grandTotal" | "wasGrandTotal">,
): { direction: "up" | "down"; by: Cents } {
  const change = subCents(plan.grandTotal, plan.wasGrandTotal);
  return change >= 0
    ? { direction: "up", by: change }
    : { direction: "down", by: cents(-change) };
}
